import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { formatCredentialTimestamp } from "../../lib/format-date";
import type { RunReport } from "./types";

type Recovery = NonNullable<RunReport["checks"][number]["recovery"]>;
type SourceLink = Recovery["sourceLinks"][number];

/** The state exclusion check links every state's list, the provider's
 * license states first; the rest wait behind a disclosure. */
const VISIBLE_LINK_COUNT = 3;

/** Official third-party lookups the app holds no copy of, so they open in a
 * new tab. */
function SourceLinkButton({ link }: { link: SourceLink }) {
  return (
    <Button asChild size="sm" variant="outline" className="h-auto py-1.5">
      <a href={link.url} target="_blank" rel="noreferrer">
        <ExternalLink className="mr-1.5 size-3.5 shrink-0" aria-hidden />
        {link.label}
      </a>
    </Button>
  );
}

function SourceLinks({ links }: { links: SourceLink[] }) {
  const visible = links.slice(0, VISIBLE_LINK_COUNT);
  const hidden = links.slice(VISIBLE_LINK_COUNT);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {visible.map((link) => (
          <SourceLinkButton key={link.url} link={link} />
        ))}
      </div>
      {hidden.length > 0 && (
        <Collapsible className="space-y-2">
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
            >
              Show {hidden.length} more official sources
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="flex flex-wrap gap-2">
            {hidden.map((link) => (
              <SourceLinkButton key={link.url} link={link} />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

/** What happened to a check that has not collected, its next retry, and how
 * to check the source by hand: what to search with, the steps, and the
 * official sites. */
export function CheckRecoveryNotice({ recovery }: { recovery: Recovery }) {
  const links = recovery.sourceLinks ?? [];
  const steps = recovery.manualInstructions ?? [];
  const details = recovery.lookupDetails ?? [];
  return (
    <section
      aria-label="Check this source manually"
      className="space-y-3 text-sm text-muted-foreground"
    >
      <p>{recovery.description}</p>
      {recovery.retry && (
        <p>
          Next attempt:{" "}
          <time dateTime={recovery.retry.nextAttemptAt}>
            {formatCredentialTimestamp(recovery.retry.nextAttemptAt)}
          </time>
          . Usually within one minute after this time.
        </p>
      )}
      {steps.length > 0 && (
        <div className="space-y-2 rounded-lg border bg-background p-3 text-foreground">
          <h4 className="text-sm font-semibold">Check it manually</h4>
          <ol className="list-decimal space-y-1 pl-5 text-xs leading-5">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          {details.length > 0 && (
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 rounded-md bg-slate-50 p-2 text-xs">
              {details.map((detail) => (
                <div key={detail.label} className="contents">
                  <dt className="text-muted-foreground">{detail.label}</dt>
                  <dd className="break-words font-medium">{detail.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {links.length > 0 && <SourceLinks links={links} />}
        </div>
      )}
      {steps.length === 0 && links.length > 0 && <SourceLinks links={links} />}
    </section>
  );
}
